import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Plus, Trash2, Edit2, X, Check, Eye, EyeOff, ArrowUp, ArrowDown, Monitor, Smartphone, Upload, ImagePlus, Sparkles, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { puoFare } from '../../utils/permessi';
import InFinestra from '../../components/InFinestra';
import { GrigliaRiquadri } from '../../components/dashboardCliente/Riquadro';
import { TIPI, tipo, STILI, DIMENSIONI, PAGINE, ESEMPIO, AUTOMATICI, idYoutube, contoFinito } from '../../components/dashboardCliente/riquadri';
import { riduciFoto } from '../../components/form/formAspetto';

const API_BASE = (process.env.REACT_APP_API_URL || '/api').replace(/\/api$/, '');
const vuoto = (t) => ({ tipo: t.id, titolo: t.titolo || '', sottotitolo: '', contenuto: '', file_nome: '', immagini: [], link_url: '', link_testo: '', stile: t.id === 'TESTO' || t.id === 'CONTO' ? 'arancio' : null, col_span: t.col, row_span: t.id === 'BANNER' ? 2 : 1, attivo: true, visibile_dal: '', visibile_al: '' });
const soloData = (d) => (d ? String(d).slice(0, 10) : '');
const dataIt = (d) => d ? new Date(d).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' }) : '';
const leggiFile = (f) => new Promise((ok, ko) => { const r = new FileReader(); r.onload = () => ok(r.result); r.onerror = () => ko(new Error('File non leggibile')); r.readAsDataURL(f); });
// ora locale per <input type="datetime-local"> (senza fuso)
const locale = (iso) => { if (!iso) return ''; const d = new Date(iso); if (isNaN(d)) return ''; const p = n => String(n).padStart(2, '0'); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`; };

// Riquadri consigliati per partire (pagina vuota)
const CONSIGLIATI = [
  { tipo: 'TESTO', titolo: 'Benvenuto in B-Cheers! 🏐', contenuto: 'Qui trovi i tuoi corsi, i pagamenti, la tessera e tutte le novità della stagione.', stile: 'arancio', col_span: 2, row_span: 1 },
  { tipo: 'CORSO', titolo: 'Il mio corso', col_span: 1 },
  { tipo: 'PAGAMENTI', titolo: 'I miei pagamenti', col_span: 1 },
  { tipo: 'TESSERA', titolo: 'La mia tessera', col_span: 1 },
  { tipo: 'FIPAV', titolo: 'Beach FIPAV', col_span: 1 },
];

// Stato di un riquadro, in parole: perché l'utente lo vede o non lo vede
const statoDi = (w) => {
  const oggi = new Date();
  if (!w.attivo) return { testo: 'Nascosto', colore: '#8E8E93' };
  if (w.visibile_dal && new Date(w.visibile_dal) > oggi) return { testo: `Dal ${dataIt(w.visibile_dal)}`, colore: '#1D6FB8' };
  if (w.visibile_al && new Date(w.visibile_al) < oggi) return { testo: 'Periodo finito', colore: '#8E8E93' };
  if (w.tipo === 'CONTO' && contoFinito(w)) return { testo: 'Evento passato', colore: '#8E8E93' };
  if (AUTOMATICI.includes(w.tipo)) return { testo: 'Automatico', colore: '#0F8B6E' };
  return { testo: w.visibile_al ? `Fino al ${dataIt(w.visibile_al)}` : 'Visibile', colore: '#0F8B6E' };
};
const visibileOra = (w) => ['Visibile', 'Automatico'].includes(statoDi(w).testo) || statoDi(w).testo.startsWith('Fino al');

// ── Anteprima: computer (pagina da 1100 px rimpicciolita) o telefono (390 px) ──────────────
function Anteprima({ widgets, modo }) {
  const box = useRef(null);
  const [zoom, setZoom] = useState(1);
  useLayoutEffect(() => {
    const el = box.current; if (!el) return;
    const misura = () => setZoom(modo === 'pc' ? Math.min(1, el.clientWidth / 1100) : 1);
    misura();
    const ro = new ResizeObserver(misura); ro.observe(el);
    return () => ro.disconnect();
  }, [modo]);
  const pagina = (
    <div style={{ background: '#F2F2F5', padding: modo === 'pc' ? '32px 36px' : '18px 14px', minHeight: modo === 'pc' ? 560 : 640 }}>
      <div className="dc-ciao"><div><h1>Ciao Giulia 👋</h1><p>{new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}</p></div></div>
      {widgets.length ? <GrigliaRiquadri widgets={widgets} dati={ESEMPIO} anteprima /> : <div className="dc-vuota"><b>Benvenuto nel portale B-Cheers</b>Qui troverai presto novità, avvisi e i tuoi dati.</div>}
    </div>
  );
  return (
    <div ref={box} style={{ width: '100%' }}>
      {modo === 'pc' ? (
        <div style={{ borderRadius: 14, overflow: 'hidden', border: '1px solid var(--border)', boxShadow: '0 10px 30px rgba(0,0,0,.08)' }}>
          <div style={{ width: 1100, zoom }}>{pagina}</div>
        </div>
      ) : (
        <div style={{ width: 390, maxWidth: '100%', margin: '0 auto', borderRadius: 36, padding: 10, background: '#1D1D1F', boxShadow: '0 20px 50px rgba(0,0,0,.2)' }}>
          <div style={{ borderRadius: 28, overflow: 'hidden', maxHeight: 760, overflowY: 'auto' }}>{pagina}</div>
        </div>
      )}
    </div>
  );
}

// ── Caricamento foto: clic, trascina o incolla. Le foto vengono ridotte nel browser. ─────────
function CaricaFoto({ multiple, onCaricate, testo }) {
  const input = useRef(null);
  const [sopra, setSopra] = useState(false);
  const [stato, setStato] = useState('');
  const carica = useCallback(async (files) => {
    const lista = [...files].filter(f => f.type.startsWith('image/'));
    if (!lista.length) { setStato('Scegli una foto (JPG, PNG, WebP)'); return; }
    const fatte = [];
    for (let i = 0; i < lista.length; i++) {
      setStato(lista.length > 1 ? `Carico ${i + 1} di ${lista.length}…` : 'Carico la foto…');
      try {
        const { dati } = await riduciFoto(lista[i], multiple ? 1800 : 2400);
        const r = await api.post('/widgets/carica', { base64: dati, nome: lista[i].name, solo_immagini: true });
        fatte.push(r.data.percorso);
      } catch (e) { setStato(e.response?.data?.error || e.message || 'Foto non caricata'); }
      if (!multiple) break;
    }
    if (fatte.length) { onCaricate(fatte); setStato(''); }
  }, [multiple, onCaricate]);
  useEffect(() => {
    const incolla = (e) => { const f = [...(e.clipboardData?.files || [])]; if (f.length) carica(f); };
    window.addEventListener('paste', incolla);
    return () => window.removeEventListener('paste', incolla);
  }, [carica]);
  return (
    <div onClick={() => input.current?.click()}
      onDragOver={e => { e.preventDefault(); setSopra(true); }} onDragLeave={() => setSopra(false)}
      onDrop={e => { e.preventDefault(); setSopra(false); carica(e.dataTransfer.files); }}
      style={{ border: `2px dashed ${sopra ? '#F4600C' : 'var(--border2)'}`, background: sopra ? 'rgba(244,96,12,.06)' : 'var(--bg-input)', borderRadius: 16, padding: '22px 16px', textAlign: 'center', cursor: 'pointer' }}>
      <input ref={input} type="file" accept="image/*" multiple={multiple} style={{ display: 'none' }} onChange={e => { carica(e.target.files); e.target.value = ''; }} />
      <ImagePlus size={28} color="#F4600C" />
      <div style={{ fontWeight: 700, marginTop: 6 }}>{testo || (multiple ? 'Aggiungi foto' : 'Scegli la foto')}</div>
      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-dim)', marginTop: 2 }}>Clicca, trascinala qui o incollala (Ctrl+V){multiple ? ' · anche più di una' : ''}</div>
      {stato && <div style={{ marginTop: 8, color: '#C24B08', fontSize: 'var(--font-size-xs)', fontWeight: 700 }}>{stato}</div>}
    </div>
  );
}

function CaricaDocumento({ nome, onCaricato }) {
  const input = useRef(null);
  const [stato, setStato] = useState('');
  const carica = async (f) => {
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) { setStato('File troppo grande (massimo 15 MB)'); return; }
    setStato('Carico il file…');
    try {
      const r = await api.post('/widgets/carica', { base64: await leggiFile(f), nome: f.name });
      onCaricato(r.data); setStato('');
    } catch (e) { setStato(e.response?.data?.error || 'File non caricato'); }
  };
  return (
    <div onClick={() => input.current?.click()} onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); carica(e.dataTransfer.files[0]); }}
      style={{ border: '2px dashed var(--border2)', background: 'var(--bg-input)', borderRadius: 16, padding: '18px 16px', textAlign: 'center', cursor: 'pointer' }}>
      <input ref={input} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp,image/*" style={{ display: 'none' }} onChange={e => { carica(e.target.files[0]); e.target.value = ''; }} />
      <Upload size={24} color="#F4600C" />
      <div style={{ fontWeight: 700, marginTop: 6 }}>{nome ? `📄 ${nome}` : 'Scegli il documento'}</div>
      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-dim)', marginTop: 2 }}>{nome ? 'Clicca per sostituirlo' : 'PDF, Word, Excel, PowerPoint o immagine · max 15 MB'}</div>
      {stato && <div style={{ marginTop: 8, color: '#C24B08', fontSize: 'var(--font-size-xs)', fontWeight: 700 }}>{stato}</div>}
    </div>
  );
}

// Bottone del riquadro: nessuno, pagina del portale o sito esterno
function SceltaBottone({ f, set }) {
  const interna = PAGINE.some(([p]) => p === f.link_url);
  const modo = !f.link_url ? '' : interna ? 'pagina' : 'sito';
  const [scelta, setScelta] = useState(modo);
  return (
    <div style={s.campo}>
      <label style={s.etichetta}>BOTTONE</label>
      <div style={s.segmenti}>
        {[['', 'Nessuno'], ['pagina', 'Pagina del portale'], ['sito', 'Sito esterno']].map(([k, n]) => (
          <button key={k} type="button" style={s.segmento(scelta === k)} onClick={() => { setScelta(k); set({ link_url: k === 'pagina' ? PAGINE[0][0] : k === 'sito' ? 'https://' : '' }); }}>{n}</button>
        ))}
      </div>
      {scelta === 'pagina' && <select style={s.input} value={f.link_url} onChange={e => set({ link_url: e.target.value })}>{PAGINE.map(([p, n]) => <option key={p} value={p}>{n}</option>)}</select>}
      {scelta === 'sito' && <input style={s.input} value={f.link_url} onChange={e => set({ link_url: e.target.value })} placeholder="https://…" />}
      {scelta && <input style={s.input} value={f.link_testo} maxLength={40} onChange={e => set({ link_testo: e.target.value })} placeholder={scelta === 'pagina' ? `Testo del bottone (es. "${(PAGINE.find(([p]) => p === f.link_url) || PAGINE[0])[1]}")` : 'Testo del bottone (es. "Iscriviti")'} />}
    </div>
  );
}

function SceltaColore({ f, set, conBianco }) {
  return (
    <div style={s.campo}>
      <label style={s.etichetta}>COLORE</label>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {conBianco && <button type="button" title="Bianco" onClick={() => set({ stile: null })} style={s.pallino('#fff', !f.stile)} />}
        {Object.entries(STILI).map(([k, v]) => <button key={k} type="button" title={v.nome} onClick={() => set({ stile: k })} style={s.pallino(v.sfondo, f.stile === k)} />)}
      </div>
    </div>
  );
}

// ── Finestra di modifica di un riquadro ───────────────────────────────────────────────
function Modifica({ iniziale, onChiudi, onSalvato }) {
  const nuovo = !iniziale.id;
  const [f, setF] = useState(iniziale.tipo ? iniziale : null);
  const [errore, setErrore] = useState('');
  const [salvo, setSalvo] = useState(false);
  const set = (p) => setF(x => ({ ...x, ...p }));

  const salva = async () => {
    setErrore('');
    const t = f.tipo;
    if (t === 'BANNER' && !f.contenuto) return setErrore('Carica la foto');
    if (t === 'CAROSELLO' && !f.immagini?.length) return setErrore('Aggiungi almeno una foto');
    if (t === 'TESTO' && !f.titolo && !f.contenuto) return setErrore('Scrivi il titolo o il testo dell\'avviso');
    if (t === 'CONTO' && !f.contenuto) return setErrore('Scegli data e ora dell\'evento');
    if (t === 'FILE' && !f.contenuto) return setErrore('Carica il documento');
    if (t === 'URL' && !/^https?:\/\//.test(f.contenuto || '')) return setErrore('Scrivi l\'indirizzo completo (inizia con https://)');
    if (f.link_url === 'https://') return setErrore('Completa l\'indirizzo del bottone o scegli "Nessuno"');
    const corpo = { ...f, contenuto: t === 'CONTO' && f.contenuto ? new Date(f.contenuto).toISOString() : f.contenuto, visibile_dal: f.visibile_dal || null, visibile_al: f.visibile_al ? `${f.visibile_al}T23:59:59` : null };
    delete corpo.id;
    setSalvo(true);
    try {
      if (nuovo) await api.post('/widgets', corpo); else await api.put(`/widgets/${iniziale.id}`, corpo);
      onSalvato();
    } catch (e) { setErrore(e.response?.data?.error || 'Non salvato'); }
    setSalvo(false);
  };

  // Primo passo di un riquadro nuovo: scelta del tipo
  if (!f) {
    const gruppo = (g, titolo, sotto) => (
      <div style={{ marginBottom: 18 }}>
        <div style={s.etichetta}>{titolo}</div>
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-dim)', margin: '2px 0 10px' }}>{sotto}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 10 }}>
          {TIPI.filter(t => t.gruppo === g).map(t => (
            <button key={t.id} type="button" onClick={() => setF(vuoto(t))} style={s.tessera}>
              <span style={{ fontSize: 26 }}>{t.icona}</span>
              <b style={{ fontSize: 'var(--font-size-sm)' }}>{t.nome}</b>
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-dim)', lineHeight: 1.35 }}>{t.desc}</span>
            </button>
          ))}
        </div>
      </div>
    );
    return (
      <div style={s.finestra}>
        <div style={s.finestraTesta}><b>Che riquadro vuoi aggiungere?</b><button style={s.x} onClick={onChiudi}><X size={18} /></button></div>
        {gruppo('contenuto', 'CONTENUTI TUOI', 'Li prepari tu: foto, avvisi, eventi, documenti.')}
        {gruppo('auto', 'AUTOMATICI · OGNUNO VEDE I SUOI DATI', 'Non devi scrivere niente: ogni utente vede i propri. Se non ha nulla da mostrare (es. non è in nessun corso) il riquadro per lui non compare.')}
      </div>
    );
  }

  const t = tipo(f.tipo);
  const auto = t.gruppo === 'auto';
  const anteprima = { ...f, id: 'anteprima', contenuto: f.tipo === 'CONTO' && f.contenuto ? new Date(f.contenuto).toISOString() : f.contenuto };
  return (
    <div style={s.finestra}>
      <div style={s.finestraTesta}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {nuovo && <button style={s.x} onClick={() => setF(null)} title="Cambia tipo"><ChevronLeft size={18} /></button>}
          <span style={{ fontSize: 22 }}>{t.icona}</span><b>{nuovo ? 'Nuovo riquadro' : 'Modifica riquadro'} · {t.nome}</b>
        </div>
        <button style={s.x} onClick={onChiudi}><X size={18} /></button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 380px)', gap: 22, alignItems: 'start' }} className="dc-editor-corpo">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {auto && <div style={s.info}>{t.desc} I dati li prende da solo il gestionale per ogni utente.{t.voce ? ' Se la voce di menu corrispondente è spenta (Impostazioni → Ruoli), il riquadro non compare.' : ''}</div>}

          {f.tipo === 'BANNER' && (
            <div style={s.campo}>
              <label style={s.etichetta}>FOTO</label>
              {f.contenuto ? (
                <div style={{ position: 'relative' }}>
                  <img src={`${API_BASE}${f.contenuto}`} alt="" style={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 14, display: 'block' }} />
                  <div style={{ marginTop: 8 }}><CaricaFoto testo="Cambia foto" onCaricate={([p]) => set({ contenuto: p })} /></div>
                </div>
              ) : <CaricaFoto onCaricate={([p]) => set({ contenuto: p })} />}
            </div>
          )}

          {f.tipo === 'CAROSELLO' && (
            <div style={s.campo}>
              <label style={s.etichetta}>FOTO ({f.immagini.length}/12) · scorrono da sole ogni 5 secondi</label>
              {f.immagini.length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {f.immagini.map((p, i) => (
                    <div key={p} style={{ position: 'relative', width: 104 }}>
                      <img src={`${API_BASE}${p}`} alt="" style={{ width: 104, height: 70, objectFit: 'cover', borderRadius: 10, display: 'block' }} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                        <button type="button" style={s.mini} disabled={!i} onClick={() => { const l = [...f.immagini]; [l[i - 1], l[i]] = [l[i], l[i - 1]]; set({ immagini: l }); }}><ChevronLeft size={14} /></button>
                        <button type="button" style={{ ...s.mini, color: '#D7262E' }} onClick={() => set({ immagini: f.immagini.filter(x => x !== p) })}><X size={14} /></button>
                        <button type="button" style={s.mini} disabled={i === f.immagini.length - 1} onClick={() => { const l = [...f.immagini]; [l[i + 1], l[i]] = [l[i], l[i + 1]]; set({ immagini: l }); }}><ChevronRight size={14} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {f.immagini.length < 12 && <CaricaFoto multiple onCaricate={(l) => setF(x => ({ ...x, immagini: [...x.immagini, ...l].slice(0, 12) }))} />}
            </div>
          )}

          <div style={s.campo}>
            <label style={s.etichetta}>{f.tipo === 'BANNER' || f.tipo === 'CAROSELLO' ? 'TITOLO SULLA FOTO (facoltativo)' : 'TITOLO'}</label>
            <input style={s.input} value={f.titolo} maxLength={120} onChange={e => set({ titolo: e.target.value })} placeholder={f.tipo === 'TESTO' ? 'es. Iscrizioni aperte! 🎉' : f.tipo === 'CONTO' ? 'es. Torneo di Natale' : t.titolo || 'es. Nuova stagione'} />
          </div>

          {['BANNER', 'CAROSELLO', 'CONTO', 'FILE', 'URL'].includes(f.tipo) && (
            <div style={s.campo}>
              <label style={s.etichetta}>{f.tipo === 'FILE' ? 'DESCRIZIONE (facoltativa)' : 'TESTO (facoltativo)'}</label>
              <textarea style={{ ...s.input, minHeight: 64, resize: 'vertical' }} value={f.sottotitolo} maxLength={600} onChange={e => set({ sottotitolo: e.target.value })} placeholder={f.tipo === 'CONTO' ? 'es. Iscrizioni entro il 15 dicembre' : f.tipo === 'FILE' ? 'es. Il regolamento dei corsi 2026/2027' : 'Una frase breve'} />
            </div>
          )}

          {f.tipo === 'TESTO' && (
            <div style={s.campo}>
              <label style={s.etichetta}>TESTO DELL'AVVISO</label>
              <textarea style={{ ...s.input, minHeight: 110, resize: 'vertical' }} value={f.contenuto} maxLength={4000} onChange={e => set({ contenuto: e.target.value })} placeholder="Scrivi il messaggio. Puoi andare a capo." />
            </div>
          )}

          {f.tipo === 'CONTO' && (
            <div style={s.campo}>
              <label style={s.etichetta}>DATA E ORA DELL'EVENTO</label>
              <input type="datetime-local" style={s.input} value={locale(f.contenuto)} onChange={e => set({ contenuto: e.target.value })} />
              <span style={s.aiuto}>Il giorno dopo l'evento il riquadro sparisce da solo.</span>
            </div>
          )}

          {f.tipo === 'FILE' && <div style={s.campo}><label style={s.etichetta}>DOCUMENTO</label><CaricaDocumento nome={f.file_nome} onCaricato={(r) => set({ contenuto: r.percorso, file_nome: r.nome })} /></div>}

          {f.tipo === 'URL' && (
            <div style={s.campo}>
              <label style={s.etichetta}>INDIRIZZO</label>
              <input style={s.input} value={f.contenuto} onChange={e => set({ contenuto: e.target.value })} placeholder="https://… (sito o video YouTube)" />
              {idYoutube(f.contenuto) ? <span style={{ ...s.aiuto, color: '#0F8B6E' }}>✓ Video YouTube: l'utente lo guarda qui, parte solo quando preme play.</span>
                : <><span style={s.aiuto}>Si apre in una nuova scheda.</span><input style={s.input} value={f.link_testo} maxLength={40} onChange={e => set({ link_testo: e.target.value })} placeholder='Testo del bottone (es. "Apri il calendario")' /></>}
            </div>
          )}

          {(f.tipo === 'TESTO' || f.tipo === 'CONTO') && <SceltaColore f={f} set={set} conBianco />}
          {['BANNER', 'CAROSELLO', 'TESTO', 'CONTO'].includes(f.tipo) && <SceltaBottone f={f} set={set} />}

          <div style={s.campo}>
            <label style={s.etichetta}>LARGHEZZA (sul computer)</label>
            <div style={s.segmenti}>
              {DIMENSIONI.map(d => <button key={d.col} type="button" style={s.segmento(f.col_span === d.col)} onClick={() => set({ col_span: d.col })}>{d.nome}<small style={{ display: 'block', fontWeight: 500, opacity: .7 }}>{d.desc}</small></button>)}
            </div>
            <div style={s.segmenti}>
              {[[1, 'Normale'], [2, 'Alto']].map(([v, n]) => <button key={v} type="button" style={s.segmento(f.row_span === v)} onClick={() => set({ row_span: v })}>{n}</button>)}
            </div>
            <span style={s.aiuto}>Sul telefono i riquadri sono sempre uno sotto l'altro.</span>
          </div>

          <div style={s.campo}>
            <label style={s.etichetta}><Calendar size={12} style={{ verticalAlign: '-2px' }} /> PERIODO (facoltativo)</label>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={s.aiuto}>dal</span><input type="date" style={{ ...s.input, width: 'auto', flex: 1 }} value={soloData(f.visibile_dal)} onChange={e => set({ visibile_dal: e.target.value })} />
              <span style={s.aiuto}>al</span><input type="date" style={{ ...s.input, width: 'auto', flex: 1 }} value={soloData(f.visibile_al)} onChange={e => set({ visibile_al: e.target.value })} />
            </div>
            <span style={s.aiuto}>Vuoto = sempre. Utile per promo e avvisi a tempo: compare e sparisce da solo.</span>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 700, cursor: 'pointer' }}>
            <input type="checkbox" checked={f.attivo} onChange={e => set({ attivo: e.target.checked })} style={{ width: 18, height: 18, accentColor: '#F4600C' }} />
            Visibile agli utenti
          </label>
        </div>

        <div style={{ position: 'sticky', top: 0 }}>
          <div style={{ ...s.etichetta, marginBottom: 8 }}>COSÌ SI VEDE (da telefono{auto ? ', dati di esempio' : ''})</div>
          <div style={{ background: '#F2F2F5', borderRadius: 18, padding: 14 }}>
            <GrigliaRiquadri widgets={[anteprima]} dati={ESEMPIO} anteprima />
          </div>
        </div>
      </div>

      {errore && <div style={{ marginTop: 14, color: '#D7262E', fontWeight: 700 }}>{errore}</div>}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
        <button style={s.btnSec} onClick={onChiudi}>Annulla</button>
        <button style={s.btnPri} onClick={salva} disabled={salvo}><Check size={15} /> {salvo ? 'Salvo…' : nuovo ? 'Aggiungi' : 'Salva'}</button>
      </div>
    </div>
  );
}

export default function Widgets() {
  const { user } = useAuth();
  const puoCreare = puoFare(user, 'dashboard_cliente', 'create');
  const puoModificare = puoFare(user, 'dashboard_cliente', 'edit');
  const puoEliminare = puoFare(user, 'dashboard_cliente', 'delete');
  const [widgets, setWidgets] = useState([]);
  const [caricato, setCaricato] = useState(false);
  const [modifica, setModifica] = useState(null);
  const [modo, setModo] = useState('pc');

  const load = () => api.get('/widgets/admin').then(r => { setWidgets(r.data); setCaricato(true); }).catch(e => { setCaricato(true); alert(e.response?.data?.error || 'Errore nel caricamento'); });
  useEffect(() => { load(); }, []);

  const apri = (w) => setModifica(w ? { ...w, titolo: w.titolo || '', sottotitolo: w.sottotitolo || '', contenuto: w.contenuto || '', file_nome: w.file_nome || '', link_url: w.link_url || '', link_testo: w.link_testo || '', immagini: Array.isArray(w.immagini) ? w.immagini : [], visibile_dal: soloData(w.visibile_dal), visibile_al: soloData(w.visibile_al) } : {});
  const sposta = async (i, d) => {
    const l = [...widgets]; [l[i], l[i + d]] = [l[i + d], l[i]]; setWidgets(l);
    try { setWidgets((await api.put('/widgets/ordine', { ids: l.map(w => w.id) })).data); } catch (e) { alert(e.response?.data?.error || 'Ordine non salvato'); load(); }
  };
  const mostra = async (w) => { try { await api.put(`/widgets/${w.id}`, { attivo: !w.attivo }); load(); } catch (e) { alert(e.response?.data?.error || 'Errore'); } };
  const elimina = async (w) => {
    if (!window.confirm(`Eliminare il riquadro "${w.titolo || tipo(w.tipo).nome}"?`)) return;
    try { await api.delete(`/widgets/${w.id}`); load(); } catch (e) { alert(e.response?.data?.error || 'Errore'); }
  };
  const consigliati = async () => {
    try { for (const c of CONSIGLIATI) await api.post('/widgets', c); load(); } catch (e) { alert(e.response?.data?.error || 'Errore'); load(); }
  };

  const visibili = widgets.filter(visibileOra);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <div>
          <h1 style={s.title}>DASHBOARD CLIENTE</h1>
          <p style={s.sub}>La Home che vedono gli utenti quando entrano nel portale. {visibili.length} riquadri visibili oggi.</p>
        </div>
        {puoCreare && <button style={s.btnPri} onClick={() => apri(null)}><Plus size={15} /> Aggiungi riquadro</button>}
      </div>

      {caricato && !widgets.length && (
        <div style={{ ...s.carta, textAlign: 'center', padding: '34px 20px', marginBottom: 20 }}>
          <Sparkles size={30} color="#F4600C" />
          <div style={{ fontWeight: 800, fontSize: 'var(--font-size-lg)', margin: '8px 0 4px' }}>La Home è vuota</div>
          <div style={{ color: 'var(--text-dim)', maxWidth: 520, margin: '0 auto 16px' }}>Parti dai riquadri consigliati: un benvenuto, il corso, i pagamenti, la tessera e il punteggio FIPAV di ogni utente. Poi aggiungi le tue foto e i tuoi avvisi.</div>
          {puoCreare && <button style={{ ...s.btnPri, margin: '0 auto' }} onClick={consigliati}><Sparkles size={15} /> Metti i riquadri consigliati</button>}
        </div>
      )}

      <div className="dc-editor" style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 400px) minmax(0, 1fr)', gap: 20, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {widgets.length > 0 && <div style={s.etichetta}>RIQUADRI · dall'alto in basso</div>}
          {widgets.map((w, i) => {
            const t = tipo(w.tipo), st = statoDi(w);
            const img = w.tipo === 'BANNER' ? w.contenuto : w.tipo === 'CAROSELLO' ? w.immagini?.[0] : null;
            return (
              <div key={w.id} style={{ ...s.carta, padding: 10, display: 'flex', alignItems: 'center', gap: 10, opacity: w.attivo ? 1 : 0.55 }}>
                <div onClick={() => puoModificare && apri(w)} style={{ width: 54, height: 54, borderRadius: 12, flexShrink: 0, display: 'grid', placeItems: 'center', fontSize: 24, cursor: puoModificare ? 'pointer' : 'default', background: img ? `center/cover url(${API_BASE}${img})` : w.stile && STILI[w.stile] ? STILI[w.stile].sfondo : 'var(--bg-input)' }}>
                  {!img && t.icona}
                </div>
                <div style={{ flex: 1, minWidth: 0, cursor: puoModificare ? 'pointer' : 'default' }} onClick={() => puoModificare && apri(w)}>
                  <div style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{w.titolo || t.titolo || t.nome}</div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-dim)', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span>{t.nome}</span><span>·</span><span>{(DIMENSIONI.find(d => d.col === w.col_span) || DIMENSIONI[0]).nome}{w.row_span > 1 ? ', alto' : ''}</span>
                    <span style={{ color: st.colore, fontWeight: 700 }}>· {st.testo}</span>
                  </div>
                </div>
                {puoModificare && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <button style={s.mini} disabled={!i} onClick={() => sposta(i, -1)} title="Più su"><ArrowUp size={14} /></button>
                    <button style={s.mini} disabled={i === widgets.length - 1} onClick={() => sposta(i, 1)} title="Più giù"><ArrowDown size={14} /></button>
                  </div>
                )}
                {puoModificare && <button style={s.mini} onClick={() => mostra(w)} title={w.attivo ? 'Nascondi' : 'Mostra'}>{w.attivo ? <Eye size={15} /> : <EyeOff size={15} />}</button>}
                {puoModificare && <button style={s.mini} onClick={() => apri(w)} title="Modifica"><Edit2 size={15} /></button>}
                {puoEliminare && <button style={{ ...s.mini, color: '#D7262E' }} onClick={() => elimina(w)} title="Elimina"><Trash2 size={15} /></button>}
              </div>
            );
          })}
          {widgets.length > 0 && <div style={{ ...s.info, marginTop: 6 }}>I riquadri automatici (corso, pagamenti, tessera, FIPAV, campo, ordini) li vede solo chi ha qualcosa da mostrare. Nell'anteprima ci sono dati di esempio.</div>}
        </div>

        <div style={{ position: 'sticky', top: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 8, flexWrap: 'wrap' }}>
            <div style={s.etichetta}>ANTEPRIMA · come la vede un utente oggi</div>
            <div style={s.segmenti}>
              <button type="button" style={s.segmento(modo === 'pc')} onClick={() => setModo('pc')}><Monitor size={14} style={{ verticalAlign: '-2px' }} /> Computer</button>
              <button type="button" style={s.segmento(modo === 'tel')} onClick={() => setModo('tel')}><Smartphone size={14} style={{ verticalAlign: '-2px' }} /> Telefono</button>
            </div>
          </div>
          <Anteprima widgets={visibili} modo={modo} />
        </div>
      </div>

      {modifica && (
        <InFinestra attiva onChiudi={() => setModifica(null)} larghezza={980}>
          <Modifica iniziale={modifica} onChiudi={() => setModifica(null)} onSalvato={() => { setModifica(null); load(); }} />
        </InFinestra>
      )}
      <style>{`@media (max-width: 1100px) { .dc-editor { grid-template-columns: minmax(0,1fr) !important; } } @media (max-width: 760px) { .dc-editor-corpo { grid-template-columns: minmax(0,1fr) !important; } }`}</style>
    </div>
  );
}

const s = {
  title: { fontFamily: "'Barlow Condensed'", fontSize: 'var(--font-size-title)', fontWeight: '800', letterSpacing: '2px', margin: 0 },
  sub: { color: 'var(--text-dim)', fontSize: 'var(--font-size-sm)', marginTop: '4px' },
  carta: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14 },
  btnPri: { display: 'flex', alignItems: 'center', gap: 8, background: '#F4600C', color: '#fff', border: 'none', borderRadius: 999, padding: '11px 20px', fontSize: 'var(--font-size-sm)', fontWeight: 700, cursor: 'pointer' },
  btnSec: { display: 'flex', alignItems: 'center', gap: 6, background: 'var(--bg-input)', color: 'var(--text)', border: '1px solid var(--border2)', borderRadius: 999, padding: '10px 18px', fontSize: 'var(--font-size-sm)', fontWeight: 700, cursor: 'pointer' },
  mini: { display: 'grid', placeItems: 'center', width: 32, height: 30, borderRadius: 8, border: 'none', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', padding: 0 },
  finestra: { padding: '20px 22px 22px' },
  finestraTesta: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, fontSize: 'var(--font-size-lg)' },
  x: { display: 'grid', placeItems: 'center', width: 36, height: 36, borderRadius: 999, border: 'none', background: 'var(--bg-input)', color: 'var(--text)', cursor: 'pointer' },
  tessera: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4, textAlign: 'left', padding: '14px 14px', borderRadius: 16, border: '1px solid var(--border2)', background: '#F7F7F9', color: 'var(--text)', cursor: 'pointer' }, // niente --bg-input: index.css lo farebbe a pillola
  campo: { display: 'flex', flexDirection: 'column', gap: 6 },
  etichetta: { fontSize: 'var(--font-size-xs)', letterSpacing: '1.5px', fontWeight: 700, color: 'var(--text-dim)' },
  aiuto: { fontSize: 'var(--font-size-xs)', color: 'var(--text-dim)' },
  info: { fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', background: 'rgba(29,111,184,.07)', borderRadius: 12, padding: '10px 12px', lineHeight: 1.45 },
  input: { background: 'var(--bg-input)', border: '1px solid var(--border2)', borderRadius: 10, padding: '10px 12px', color: 'var(--text)', fontSize: 'var(--font-size-sm)', outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' },
  segmenti: { display: 'flex', gap: 4, padding: 4, background: 'var(--bg-input)', borderRadius: 12, flexWrap: 'wrap' },
  segmento: (on) => ({ flex: 1, minWidth: 0, padding: '8px 10px', borderRadius: 9, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 'var(--font-size-xs)', background: on ? '#fff' : 'transparent', color: on ? '#C24B08' : 'var(--text-muted)', boxShadow: on ? '0 1px 4px rgba(0,0,0,.1)' : 'none' }),
  pallino: (sfondo, on) => ({ width: 36, height: 36, borderRadius: 999, border: on ? '3px solid #1D1D1F' : '1px solid var(--border2)', background: sfondo, cursor: 'pointer', padding: 0, boxShadow: on ? '0 0 0 2px #fff inset' : 'none' }),
};
